import { FC, useState, useEffect } from "react";

import TextField from "@material-ui/core/TextField";
import CheckIcon from "@material-ui/icons/Check";
import { Typography } from "@portex-pro/ui-components";
import partnerConnectSVG from "assets/partner-connect.svg";
import { useTranslation } from "react-i18next";
import { CSSTransition, SwitchTransition } from "react-transition-group";

import PortexDialog from "../../../components/PortexDialog";
import { validateEmail } from "../../../utils/validateEmail";

import "../css/PartnerConnectDialog.css";

type PartnerConnectDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm?: (email: string) => void;
  partnerName: string;
  partnerLogo: string;
  portexLogo?: string;
  permissions?: string[];
  description?: string;
  showCancelButton?: boolean;
};

const PartnerConnectDialog: FC<PartnerConnectDialogProps> = ({
  open,
  onClose,
  onConfirm,
  partnerName,
  partnerLogo,
  portexLogo,
  permissions = [],
  description,
  showCancelButton = true,
}) => {
  const [step, setStep] = useState<"permissions" | "email">("permissions");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const { t } = useTranslation(["benchmarkRateEstimates"]);

  useEffect(() => {
    if (!open) {
      setStep("permissions");
      setEmail("");
      setError("");
    }
  }, [open]);

  const handleContinue = () => {
    setStep("email");
  };

  const handleConnect = () => {
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    onConfirm?.(email);
    onClose();
  };

  const renderDialogContent = () => (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
        <img src={partnerLogo} alt={partnerName} style={{ height: 88 }} />
        <img src={partnerConnectSVG} alt="Connect icon" style={{ marginLeft: 20, marginRight: 20 }} />
        {portexLogo ? (
          <img src={portexLogo} alt="Portex" style={{ height: 88 }} />
        ) : (
          <Typography variant="body1" style={{ fontWeight: "bold" }}>
            Portex
          </Typography>
        )}
      </div>

      <SwitchTransition mode="out-in">
        <CSSTransition key={step} timeout={300} classNames="fade-slide" unmountOnExit>
          {step === "permissions" ? (
            <div>
              <Typography variant="body1" paragraph>
                {description || t("benchmarkRateEstimates:connectDescription", { partner: partnerName })}
              </Typography>
              <ul style={{ listStyle: "none", paddingLeft: 0, marginBottom: 0 }}>
                {permissions.map((perm, idx) => (
                  <li key={idx} style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
                    <CheckIcon style={{ color: "#0045FF", marginRight: 8 }} />
                    <Typography variant="body1">{perm}</Typography>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div>
              <Typography variant="body1" paragraph>
                {t("benchmarkRateEstimates:enterEmailAddress", "Please enter your email address to continue")}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                label={t("benchmarkRateEstimates:emailAddressLabel", "Email address")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!error}
                helperText={error || ""}
                autoFocus
              />
            </div>
          )}
        </CSSTransition>
      </SwitchTransition>
    </div>
  );

  return (
    <PortexDialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      title={t("benchmarkRateEstimates:connectDialogHeader", { partner: partnerName })}
      renderDialogContent={renderDialogContent}
      hideCancelButton={!showCancelButton}
      cancelButtonCopy={t("benchmarkRateEstimates:cancelButton", "Cancel")}
      confirmButtonCopy={
        step === "permissions"
          ? t("benchmarkRateEstimates:continueButton", "Continue")
          : t("benchmarkRateEstimates:connectButton", "Connect")
      }
      onClickConfirm={step === "permissions" ? handleContinue : handleConnect}
    />
  );
};

export default PartnerConnectDialog;
